export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ClampedSelectionRect extends Rect {
  /** Which edges of the visible rect are where the canvas cuts the overlay
   * off (vs. the overlay's true edge, whose real selection border already
   * shows there). Only clipped edges need the dashed treatment. */
  clipped: { left: boolean; top: boolean; right: boolean; bottom: boolean };
}

/**
 * Selection indicator for overlays that extend beyond the canvas (oversized
 * template gradients live mostly off-canvas, so their true selection border
 * gets clipped to nothing). Returns the visible intersection of the overlay
 * rect with the canvas — where to draw the dashed outline — or null when no
 * indicator is needed: the overlay sits fully inside the canvas (its real
 * border is visible) or fully outside it (nothing to outline).
 */
export function clampRectToCanvas(
  rect: Rect,
  canvasWidth: number,
  canvasHeight: number
): ClampedSelectionRect | null {
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;

  const clipped = {
    left: rect.left < 0,
    top: rect.top < 0,
    right: right > canvasWidth,
    bottom: bottom > canvasHeight,
  };
  if (!clipped.left && !clipped.top && !clipped.right && !clipped.bottom) {
    return null;
  }

  const visibleLeft = Math.max(rect.left, 0);
  const visibleTop = Math.max(rect.top, 0);
  const visibleWidth = Math.min(right, canvasWidth) - visibleLeft;
  const visibleHeight = Math.min(bottom, canvasHeight) - visibleTop;
  if (visibleWidth <= 0 || visibleHeight <= 0) return null;

  return {
    left: visibleLeft,
    top: visibleTop,
    width: visibleWidth,
    height: visibleHeight,
    clipped,
  };
}
